import { useState, useEffect, useCallback } from "react";
import { fetchOdalar, fetchRezervasyonlar, fetchMusteriler, type Oda, type Rezervasyon, type Musteri } from "@/services/api";

const RENK_MAP: Record<string, { bg: string; text: string; label: string }> = {
  "rezervasyon_onaylandi": { bg: "bg-primary-400", text: "text-white", label: "Onaylı" },
  "kapora_bekleniyor": { bg: "bg-yellow-400", text: "text-white", label: "Kapora" },
  "konakliyor": { bg: "bg-accent-500", text: "text-white", label: "Konuklıyor" },
  "giris_yapildi": { bg: "bg-accent-500", text: "text-white", label: "Giriş Yapıldı" },
  "cikis_yapildi": { bg: "bg-background-400", text: "text-foreground-700", label: "Çıktı" },
  "iptal_edildi": { bg: "bg-red-400", text: "text-white", label: "İptal" },
  "teklif_verildi": { bg: "bg-orange-400", text: "text-white", label: "Teklif" },
};

export default function CalendarPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [odalar, setOdalar] = useState<Oda[]>([]);
  const [rezervasyonlar, setRezervasyonlar] = useState<Rezervasyon[]>([]);
  const [musteriler, setMusteriler] = useState<Musteri[]>([]);
  const [yil, setYil] = useState(new Date().getFullYear());
  const [ay, setAy] = useState(new Date().getMonth());

  const loadData = useCallback(async () => {
    try { setLoading(true); setError(null);
      const [o, r, m] = await Promise.all([fetchOdalar(), fetchRezervasyonlar(), fetchMusteriler()]);
      setOdalar(o.filter(x => x.aktif)); setRezervasyonlar(r); setMusteriler(m);
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Hata"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const gunSayisi = getDaysInMonth(yil, ay);
  const gunler = Array.from({ length: gunSayisi }, (_, i) => i + 1);

  const AY_ADLARI = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

  const oncekiAy = () => { if (ay === 0) { setAy(11); setYil(yil - 1); } else setAy(ay - 1); };
  const sonrakiAy = () => { if (ay === 11) { setAy(0); setYil(yil + 1); } else setAy(ay + 1); };

  const getRezForOdaAndDay = (odaId: number, gun: number) => {
    const tarih = `${yil}-${String(ay + 1).padStart(2, "0")}-${String(gun).padStart(2, "0")}`;
    return rezervasyonlar.find(r => {
      if (r.oda_id !== odaId) return false;
      if (r.silindi) return false;
      const giris = new Date(r.giris_tarihi);
      const cikis = new Date(r.cikis_tarihi);
      const hedef = new Date(tarih);
      return hedef >= giris && hedef < cikis;
    });
  };

  const isGirisGunu = (odaId: number, gun: number) => {
    const tarih = `${yil}-${String(ay + 1).padStart(2, "0")}-${String(gun).padStart(2, "0")}`;
    return rezervasyonlar.some(r => r.oda_id === odaId && r.giris_tarihi === tarih && !r.silindi);
  };

  const getMusteriAd = (id: number) => musteriler.find(m => m.id === id)?.ad_soyad || "";

  const ayinDolulukOrani = () => {
    let toplamGun = odalar.length * gunSayisi;
    let doluGun = 0;
    odalar.forEach(o => { gunler.forEach(g => { if (getRezForOdaAndDay(o.id, g)) doluGun++; }); });
    return toplamGun > 0 ? Math.round((doluGun / toplamGun) * 100) : 0;
  };

  const buAyGirisler = rezervasyonlar.filter(r => { const d = new Date(r.giris_tarihi); return d.getFullYear() === yil && d.getMonth() === ay && !r.silindi; });
  const tahminiGelir = buAyGirisler.reduce((sum, r) => sum + Number(r.toplam_tutar || 0), 0);

  const today = new Date().toISOString().split("T")[0];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="flex flex-col items-center gap-3"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div><p className="text-sm text-foreground-500">Yükleniyor...</p></div></div>;
  if (error) return <div className="flex items-center justify-center h-64"><div className="flex flex-col items-center gap-4"><p className="text-sm text-foreground-600">{error}</p><button onClick={loadData} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">Tekrar Dene</button></div></div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-xl font-semibold text-foreground-900 font-heading">Takvim / Doluluk</h1><p className="text-sm text-foreground-500 mt-0.5">Oda bazlı doluluk durumunu tarih aralığına göre buradan kontrol edebilirsiniz</p></div>
        <button className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-add-line text-sm"></i> Yeni Rezervasyon</button>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <button onClick={oncekiAy} className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center hover:bg-background-300 transition-colors cursor-pointer"><i className="ri-arrow-left-s-line text-foreground-600"></i></button>
          <h3 className="text-base font-semibold text-foreground-900 font-heading">{AY_ADLARI[ay]} {yil}</h3>
          <button onClick={sonrakiAy} className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center hover:bg-background-300 transition-colors cursor-pointer"><i className="ri-arrow-right-s-line text-foreground-600"></i></button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4"><p className="text-xs text-foreground-500 mb-1">Ay Doluluk Oranı</p><p className="text-2xl font-semibold text-primary-700 font-heading">%{ayinDolulukOrani()}</p></div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4"><p className="text-xs text-foreground-500 mb-1">Bu Ay Rezervasyon</p><p className="text-2xl font-semibold text-foreground-900 font-heading">{buAyGirisler.length}</p></div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4"><p className="text-xs text-foreground-500 mb-1">Toplam Oda</p><p className="text-2xl font-semibold text-accent-600 font-heading">{odalar.length}</p></div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4"><p className="text-xs text-foreground-500 mb-1">Tahmini Gelir</p><p className="text-xl font-semibold text-primary-700 font-heading">₺{Math.round(tahminiGelir / 1000)}K</p></div>
      </div>

      <div className="flex flex-wrap gap-3">
        {Object.entries(RENK_MAP).map(([key, val]) => (<div key={key} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-sm ${val.bg}`}></span><span className="text-xs text-foreground-600">{val.label}</span></div>))}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse" style={{ minWidth: "900px" }}>
            <thead>
              <tr className="border-b border-background-200">
                <th className="sticky left-0 z-10 bg-background-100 px-4 py-3 text-left font-semibold text-foreground-600 whitespace-nowrap min-w-[140px] border-r border-background-200">Oda</th>
                {gunler.map(g => { const ts = `${yil}-${String(ay + 1).padStart(2, "0")}-${String(g).padStart(2, "0")}`; const isToday = ts === today; return (<th key={g} className={`px-1 py-3 text-center font-medium whitespace-nowrap border-r border-background-200/70 w-9 ${isToday ? "bg-primary-100 text-primary-700" : "bg-background-100/70 text-foreground-500"}`}>{g}</th>); })}
              </tr>
            </thead>
            <tbody>
              {odalar.map(oda => (
                <tr key={oda.id} className="border-b border-background-100 hover:bg-background-50/80 transition-colors">
                  <td className="sticky left-0 z-10 bg-background-50 px-4 py-2.5 border-r border-background-200 whitespace-nowrap"><div><p className="font-medium text-foreground-800">{oda.oda_no}</p><p className="text-[10px] text-foreground-400">{oda.oda_tipi_adi}</p></div></td>
                  {gunler.map(gun => {
                    const rez = getRezForOdaAndDay(oda.id, gun);
                    const isGiris = isGirisGunu(oda.id, gun);
                    const renk = rez ? RENK_MAP[rez.durum] : null;
                    const ts = `${yil}-${String(ay + 1).padStart(2, "0")}-${String(gun).padStart(2, "0")}`;
                    const isToday = ts === today;
                    return (<td key={gun} title={rez ? `${getMusteriAd(rez.musteri_id)} — ${renk?.label || rez.durum}` : "Boş"} className={`border-r border-background-200/50 h-10 text-center relative cursor-pointer hover:opacity-80 transition-opacity ${renk ? renk.bg : isToday ? "bg-primary-50/40" : ""}`}>
                      {rez && isGiris && <span className="absolute left-0.5 top-0.5 w-1.5 h-1.5 bg-white/80 rounded-full"></span>}
                      {rez && <span className={`text-[9px] font-medium ${renk?.text || ""}`}>{isGiris ? getMusteriAd(rez.musteri_id).split(" ")[0].slice(0, 6) : ""}</span>}
                    </td>);
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-xs text-foreground-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-white/80 rounded-full border border-foreground-300"></span> Beyaz nokta giriş gününü gösterir</div>
    </div>
  );
}